// src/utils/useApplePay.ts

import { tracker } from 'ouisys-engine/utilities/tracker';
import { validateMerchant, processPayment } from '../services/applePayService';
import { useRootContext } from '../providers/RootContext';
import searchToObject from '../utils/searchToObject';
import { ApplePayMerchantSession } from './types';
import { useRef } from 'react';

const useApplePay = () => {
  const { setErrorMessage, config, locale, setIsLoading } = useRootContext();
  const urlParams = searchToObject();
  const { visitor } = (window as any).pac_analytics || {};
  const rockmanId = visitor?.rockmanId || '';
  const slug = `${config?.strategyConfigs?.default?.flowConfig?.slug}${urlParams['d_country'] || undefined}`;
  const isProcessing = useRef(false);

  const handleError = (message: string, error: string = ''): void => {
    if (urlParams['debug']) {
      const fullMessage = error ? `${message}: ${error}` : message;
      alert(fullMessage);
      console.error(fullMessage);
    }
    setErrorMessage('generic-error');
  };

  const validateMerchantHandler = async (applePaySession: ApplePaySession, validationURL: string): Promise<void> => {
    tracker.customEvent('apple-pay-flow', 'fetch-merchant-sessions', 'fetch-init', {
      validationURL
    });

    try {
      const merchantSession: ApplePayMerchantSession = await validateMerchant(validationURL, rockmanId, slug);

      const {
        merchantSessionIdentifier,
        merchantIdentifier,
        signature,
        operationalAnalyticsIdentifier,
        pspId,
        ...filteredMerchantSession
      } = merchantSession;

      tracker.customEvent('apple-pay-flow', 'fetch-merchant-sessions', 'fetch-result', {
        validationURL,
        merchantSession: filteredMerchantSession
      });

      try {
        applePaySession.completeMerchantValidation(merchantSession);
      } catch (error) {
        tracker.customEvent('apple-pay-flow', 'fetch-merchant-sessions', 'completing-merchant-validation-error', {
          validationURL,
          error: error?.toString(),
          merchantSession: filteredMerchantSession
        });
        throw new Error(error);
      }
    } catch (error: any) {
      tracker.customEvent('apple-pay-flow', 'fetch-merchant-sessions', 'fetch-catch', {
        validationURL,
        error: error?.toString()
      });
      setErrorMessage('generic-error');
      handleError('Merchant validation failed.', error?.toString());
    }
  };

  const processPaymentHandler = async (
    applePaySession: ApplePaySession,
    payment: ApplePayJS.ApplePayPayment
  ): Promise<void> => {
    tracker.customEvent('apple-pay-flow', 'process-payment', 'fetch-init', {
      payment
    });

    try {
      const result = await processPayment(payment, slug, locale, rockmanId);

      if (result.status === 'error') {
        tracker.customEvent('apple-pay-flow', 'process-payment', 'fetch-error', {
          payment,
          result
        });
        throw new Error('Payment processing failed.');
      } else {
        tracker.customEvent('apple-pay-flow', 'process-payment', 'fetch-result', {
          payment,
          result
        });
        applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
        tracker.advancedInFlow('apple-pay-flow', 'payment-success');
        // if (result.redirect) {
        //   window.location.href = result.redirect;
        // }
        if (result.html) {
          document.open();
          document.write(result.html);
          document.close();
        } else {
          console.error('No redirect HTML received');
        }
      }
    } catch (error: any) {
      tracker.customEvent('apple-pay-flow', 'process-payment', 'fetch-catch', {
        payment,
        error: error?.toString()
      });
      applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
      tracker.recedeInFlow('apple-pay-flow', 'payment-failed');
      handleError('Payment processing failed.', error?.toString());
      setErrorMessage('generic-error');
    }
  };

  const initApplePay = async (paymentRequest, trigger: string): Promise<void> => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    setIsLoading(true);
    tracker.advancedInPreFlow(trigger, {
      paymentRequest
    });
    if (urlParams['test-error']) {
      setErrorMessage('generic-error');
      return;
    }

    try {
      const applePaySession = new ApplePaySession(3, paymentRequest);

      applePaySession.onvalidatemerchant = (event) => {
        tracker.advancedInFlow('apple-pay-flow', 'merchant-validation-success');
        validateMerchantHandler(applePaySession, event.validationURL);
      };

      applePaySession.onpaymentauthorized = (event) => {
        tracker.advancedInFlow('apple-pay-flow', 'payment-flow-authorized');
        processPaymentHandler(applePaySession, event.payment);
      };

      applePaySession.oncancel = (event) => {
        tracker.recedeInFlow('apple-pay-flow', 'payment-flow-canceled');
      };

      applePaySession.begin();
      tracker.advancedInFlow('apple-pay-flow', 'begin', {
        paymentRequest
      });
      setTimeout(() => {
        setIsLoading(false);
        isProcessing.current = false;
      }, 3000);
    } catch (error: any) {
      tracker.recedeInFlow('apple-pay-flow', 'payment-flow-catch', {
        error: error?.toString()
      });
      handleError('An exception occurred during Apple Pay session.', error.toString());
      setErrorMessage('generic-error');
      setTimeout(() => {
        setIsLoading(false);
        isProcessing.current = false;
      }, 2000);
    }
  };

  return {
    initApplePay
  };
};

export default useApplePay;
